import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { vendorQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, linkBtn } from './VendorDetails';
import PhoneNumberInput from '../../components/PhoneNumberInput';

type ContactType = 'security' | 'incident' | 'account' | 'privacy' | 'legal' | 'billing' | 'technical' | 'executive';

interface Contact {
  id: string;
  name: string | null;
  role: string | null;
  email: string | null;
  phone: string | null;
  contact_types: ContactType[];
  notes: string | null;
  source: 'onboarding' | 'added';
  is_primary: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

interface Props {
  vendorId: string;
  vendorName?: string;
  isDark: boolean;
}

/* Responsibilities. Having a primary contact plus the first six (COVERAGE_ROLES) are the key roles whose coverage is shown. */
const TYPES: { value: ContactType; label: string; hint: string; hue: [string, string, string, string] }[] = [
  { value: 'security', label: 'Security', hint: 'Security questions, assessments, vulnerabilities', hue: ['#6D28D9', '#F3F0FF', '#C4B5FD', '#A78BFA'] },
  { value: 'incident', label: 'Incident', hint: 'Breach and incident notification', hue: ['#B91C1C', '#FEF2F2', '#FECACA', '#F87171'] },
  { value: 'account', label: 'Account', hint: 'Account or relationship manager', hue: ['#0F766E', '#F0FDFA', '#99F6E4', '#2DD4BF'] },
  { value: 'privacy', label: 'Privacy', hint: 'Data protection officer, privacy requests', hue: ['#1D4ED8', '#EFF6FF', '#BFDBFE', '#60A5FA'] },
  { value: 'legal', label: 'Legal', hint: 'Contracts, legal notices', hue: ['#9A3412', '#FFF7ED', '#FED7AA', '#FB923C'] },
  { value: 'billing', label: 'Billing', hint: 'Invoices and payments', hue: ['#854D0E', '#FEFCE8', '#FDE68A', '#FACC15'] },
  { value: 'technical', label: 'Technical', hint: 'Integration and technical support', hue: ['#334155', '#F1F5F9', '#CBD5E1', '#94A3B8'] },
  { value: 'executive', label: 'Executive', hint: 'Executive sponsor or escalation', hue: ['#9D174D', '#FDF2F8', '#FBCFE8', '#F472B6'] },
];
const typeMeta = (t: string) => TYPES.find(x => x.value === t);

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg).filter(Boolean).join('; ') || fallback;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return fallback;
};
const initials = (c: Contact) => {
  const parts = (c.name || c.email || '?').trim().split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts.length > 1 ? parts[1][0] : '')).toUpperCase();
};
const relative = (iso?: string | null) => {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 1) return 'today';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
// CSV cells starting with = + - @ would run as formulas in a spreadsheet.
const csvCell = (v: string | null | undefined) => {
  let s = (v ?? '').toString();
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const Icon = ({ d, size = 14, style }: { d: string; size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}><path d={d} /></svg>
);
const I = {
  plus: 'M12 5v14M5 12h14',
  check: 'M5 12l5 5L20 7',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 6l-10 7L2 6',
  phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z',
  dots: 'M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM19 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM5 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  close: 'M6 6l12 12M18 6 6 18',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z',
  star: 'M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2Z',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6',
  note: 'M4 4h16v12H8l-4 4V4Z',
  users: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
};

type Draft = { name: string; role: string; email: string; phone: string; contact_types: ContactType[]; notes: string };
const EMPTY: Draft = { name: '', role: '', email: '', phone: '', contact_types: [], notes: '' };

interface Involvement { kind: 'questionnaire' | 'remediation'; id: string; title: string; status: string; at: string | null }

const COVERAGE_ROLES: ContactType[] = ['security', 'incident', 'account', 'privacy', 'legal', 'billing'];
const statusLabel = (s: string) => s.replace(/_/g, ' ').replace(/^./, x => x.toUpperCase());

export default function VendorContacts({ vendorId, vendorName, isDark, onOpenQuestionnaires, onOpenRemediation }: Props & {
  onOpenQuestionnaires?: () => void; onOpenRemediation?: (requestId: string) => void;
}) {
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const canDelete = !!user && (user.role === 'admin' || user.role === 'super_admin');
  const queryClient = useQueryClient();

  const contactsQuery = useQuery(vendorQueries.contacts(vendorId));
  const contacts: Contact[] = useMemo(
    () => (contactsQuery.data ?? []).map((x: any) => ({ ...x, contact_types: x.contact_types || [] })),
    [contactsQuery.data],
  );
  // Which questionnaires / remediation requests went to each contact's email (read-only, matched by email).
  const involvementQuery = useQuery({
    queryKey: ['vendors', 'detail', vendorId, 'contacts-involvement'],
    queryFn: async () => ((await api.get(`/v1/vendors/${vendorId}/contacts/involvement`)).data?.by_email || {}) as Record<string, Involvement[]>,
    enabled: !!vendorId,
  });
  const involvementOf = (x: Contact): Involvement[] => (x.email ? involvementQuery.data?.[x.email.toLowerCase()] || [] : []);

  // The primary contact lives on the vendor row itself, so contact writes can change any vendor view.
  const saveMutation = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
      id ? api.put(`/v1/vendors/${vendorId}/contacts/${id}`, body) : api.post(`/v1/vendors/${vendorId}/contacts`, body),
    meta: { invalidates: [['vendors']] },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/vendors/${vendorId}/contacts/${id}`),
    meta: { invalidates: [['vendors']] },
  });
  const primaryMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/v1/vendors/${vendorId}/contacts/${id}/make-primary`)).data,
    onSuccess: data => queryClient.setQueryData(vendorQueries.contacts(vendorId).queryKey, data?.contacts || []),
    meta: { invalidates: [['vendors']] },
  });

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ContactType | 'all'>('all');
  const [view, setView] = useState<'table' | 'cards'>('table');
  const [editing, setEditing] = useState<{ target: Contact | 'new'; preset?: ContactType } | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Contact | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuFor]);

  const c = {
    ink: isDark ? '#e2e8f0' : T.ink,
    muted: isDark ? '#94a3b8' : T.slate,
    faint: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr,
    line2: isDark ? '#161616' : '#F1F5F9',
    soft: isDark ? '#141414' : T.surface2,
    surface: isDark ? '#0d0d0d' : T.surface,
    accent: isDark ? '#60A5FA' : '#185FA5',
    accentBg: isDark ? 'rgba(59,130,246,.14)' : '#E6F1FB',
    accentInk: isDark ? '#BFDBFE' : '#0C447C',
    red: isDark ? '#F87171' : T.red,
    sel: isDark ? '#0b1a2e' : '#F5F9FE',
  };
  const tagStyle = (t: ContactType): React.CSSProperties => {
    const [fg, bg, , darkFg] = typeMeta(t)!.hue;
    return {
      display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: sans,
      color: isDark ? darkFg : fg, background: isDark ? `${fg}26` : bg, whiteSpace: 'nowrap',
    };
  };
  const avatarColors = (x: Contact): [string, string] => {
    const first = x.contact_types[0] && typeMeta(x.contact_types[0]);
    if (x.is_primary || !first) return [c.accentBg, c.accentInk];
    const [fg, bg, , darkFg] = first.hue;
    return isDark ? [`${fg}26`, darkFg] : [bg, fg];
  };
  const btn = (kind: 'default' | 'primary' | 'ghost' | 'danger' = 'default'): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans, whiteSpace: 'nowrap',
    ...(kind === 'primary' ? { border: '1px solid #185FA5', background: '#185FA5', color: '#fff' }
      : kind === 'ghost' ? { border: '1px solid transparent', background: 'transparent', color: c.muted }
      : kind === 'danger' ? { border: `1px solid ${c.line}`, background: c.surface, color: c.red }
      : { border: `1px solid ${c.line}`, background: c.surface, color: c.ink }),
  });
  const Avatar = ({ x, size = 34 }: { x: Contact; size?: number }) => {
    const [bg, fg] = avatarColors(x);
    return (
      <span style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: Math.round(size * 0.37), fontWeight: 700, color: fg, background: bg }}>{initials(x)}</span>
    );
  };
  const copy = (text: string, what: string) => { navigator.clipboard?.writeText(text); setToast(`${what} copied`); };

  /* coverage */
  const primary = contacts.find(x => x.is_primary) || null;
  const slots = [
    { key: 'primary' as const, label: 'Primary', people: primary ? [primary] : [] },
    ...COVERAGE_ROLES.map(r => ({ key: r, label: typeMeta(r)!.label, people: contacts.filter(x => x.contact_types.includes(r)) })),
  ];
  const coveredCount = slots.filter(x => x.people.length > 0).length;

  /* filtering */
  const presentTypes = TYPES.filter(t => contacts.some(x => x.contact_types.includes(t.value)));
  const q = search.trim().toLowerCase();
  const visible = contacts.filter(x =>
    (filter === 'all' || x.contact_types.includes(filter)) &&
    (!q || [x.name, x.email, x.role, x.phone, x.notes, ...x.contact_types].some(v => v && v.toLowerCase().includes(q))),
  );
  const viewing = viewingId ? contacts.find(x => x.id === viewingId) || null : null;

  const exportCsv = () => {
    const rows = [['Name', 'Job title', 'Email', 'Phone', 'Responsibilities', 'Primary', 'Notes'],
      ...contacts.map(x => [x.name, x.role, x.email, x.phone, x.contact_types.map(t => typeMeta(t)?.label).join('; '), x.is_primary ? 'Yes' : '', x.notes])];
    const blob = new Blob(['﻿' + rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(vendorName || 'vendor').replace(/[^\w-]+/g, '_')}_contacts.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const makePrimary = async (contact: Contact) => {
    try {
      await primaryMutation.mutateAsync(contact.id);
      setToast(`${contact.name || contact.email} is now the primary contact`);
    } catch (e) {
      setToast(apiError(e, "Couldn't change the primary contact."));
    }
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteError('');
    try {
      await deleteMutation.mutateAsync(deleting.id);
      setToast(`${deleting.name || deleting.email} removed`);
      if (viewingId === deleting.id) setViewingId(null);
      setDeleting(null);
    } catch (e) {
      setDeleteError(apiError(e, "Couldn't delete the contact. Try again."));
    }
  };
  const menuItems = (contact: Contact) => [
    canEdit && { label: 'Edit', icon: I.edit, run: () => setEditing({ target: contact }) },
    canEdit && !contact.is_primary && { label: 'Make primary', icon: I.star, run: () => makePrimary(contact) },
    contact.email && { label: 'Copy email', icon: I.copy, run: () => copy(contact.email!, 'Email') },
    canDelete && !contact.is_primary && { label: 'Delete', icon: I.trash, danger: true, run: () => { setDeleting(contact); setDeleteError(''); } },
  ].filter(Boolean) as { label: string; icon: string; danger?: boolean; run: () => void }[];

  const RowMenu = ({ contact }: { contact: Contact }) => {
    const items = menuItems(contact);
    if (!items.length) return null;
    return (
      <span style={{ position: 'relative', display: 'inline-flex' }} onClick={e => e.stopPropagation()}>
        <button aria-label="Contact actions" onMouseDown={e => e.stopPropagation()} onClick={() => setMenuFor(m => (m === contact.id ? null : contact.id))}
          style={{ border: 'none', background: menuFor === contact.id ? c.soft : 'transparent', color: c.faint, cursor: 'pointer', borderRadius: 6, padding: 4, display: 'flex' }}>
          <Icon d={I.dots} size={16} />
        </button>
        {menuFor === contact.id && (
          <div className="vc-menu" onMouseDown={e => e.stopPropagation()} style={{
            position: 'absolute', top: 28, right: 0, zIndex: 20, minWidth: 170, padding: 4, borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface,
            boxShadow: isDark ? '0 10px 30px rgba(0,0,0,.6)' : '0 10px 30px rgba(15,23,42,.12)', animation: 'vcFade .12s ease-out',
          }}>
            {items.map(item => (
              <button key={item.label} onClick={() => { setMenuFor(null); item.run(); }} style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px', border: 'none', background: 'transparent', borderRadius: 6,
                fontSize: 12.5, fontWeight: 500, color: item.danger ? c.red : c.ink, cursor: 'pointer', fontFamily: sans, textAlign: 'left',
              }}><Icon d={item.icon} size={13} />{item.label}</button>
            ))}
          </div>
        )}
      </span>
    );
  };
  const involvementText = (x: Contact) => {
    const items = involvementOf(x);
    if (!items.length) return '—';
    const qn = items.filter(i => i.kind === 'questionnaire').length, rn = items.filter(i => i.kind === 'remediation').length;
    return [qn && `${qn} questionnaire${qn === 1 ? '' : 's'}`, rn && `${rn} remediation`].filter(Boolean).join(' · ');
  };

  /* ── states ── */
  if (contactsQuery.isPending) {
    return <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: c.muted }}>Loading contacts…</div>;
  }
  if (contactsQuery.isError) {
    return (
      <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: c.red }}>
        {apiError(contactsQuery.error, "Couldn't load the contacts.")} <button style={linkBtn(isDark)} onClick={() => contactsQuery.refetch()}>Retry</button>
      </div>
    );
  }

  const th: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: c.muted, textAlign: 'left', padding: '10px 14px', borderBottom: `1px solid ${c.line}`,
    background: isDark ? '#111' : '#FBFCFE', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '12px 14px', borderBottom: `1px solid ${c.line2}`, verticalAlign: 'middle', fontSize: 12.5 };

  return (
    <div style={{ display: 'grid', gap: 12, fontFamily: sans }}>
      <style>{`
        @keyframes vcDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes vcFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        .vc-card:hover { border-color: ${isDark ? '#334155' : '#CBD5E1'} !important; }
        .vc-menu button:hover { background: ${c.soft} !important; }
        .vc-row:hover td { background: ${c.soft}; }
        @media (max-width: 1100px) { .vc-top { grid-template-columns: minmax(0,1fr) !important; } .vc-slots { grid-template-columns: repeat(2, minmax(0,1fr)) !important; } }
      `}</style>

      {/* 1 Header */}
      <div style={{ ...card(isDark), display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>Contacts</div>
          <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
            The people you work with{vendorName ? ` at ${vendorName}` : ''} · {contacts.length} contact{contacts.length === 1 ? '' : 's'} · {coveredCount} of {slots.length} key roles covered
          </div>
        </div>
        <button style={{ ...btn(), opacity: contacts.length ? 1 : 0.5 }} disabled={!contacts.length} onClick={exportCsv}><Icon d={I.download} size={13} />Export CSV</button>
        {canEdit && <button style={btn('primary')} onClick={() => setEditing({ target: 'new' })}><Icon d={I.plus} size={13} />Add contact</button>}
      </div>

      {/* 2 Primary contact | 3 Key roles */}
      <div className="vc-top" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,2fr)', gap: 12 }}>
        <div style={{ ...card(isDark), display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>Primary contact</span>
            {primary && <span style={{ ...tagPill(c.accentBg, c.accentInk) }}>★ Primary</span>}
          </div>
          {primary ? (
            <>
              <button onClick={() => setViewingId(primary.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: sans }}>
                <Avatar x={primary} size={46} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: c.ink }}>{primary.name || primary.email || 'Unnamed contact'}</span>
                  <span style={{ display: 'block', fontSize: 12, color: primary.role ? c.muted : c.faint }}>{primary.role || 'No job title'}</span>
                </span>
              </button>
              <div style={{ display: 'grid', gap: 8, marginTop: 14, fontSize: 12.5 }}>
                <InfoLine label="Email" value={primary.email} href={primary.email ? `mailto:${primary.email}` : undefined} c={c} onCopy={primary.email ? () => copy(primary.email!, 'Email') : undefined} />
                <InfoLine label="Phone" value={primary.phone} href={primary.phone ? `tel:${primary.phone.replace(/[^\d+]/g, '')}` : undefined} c={c} onCopy={primary.phone ? () => copy(primary.phone!, 'Phone') : undefined} />
              </div>
              {primary.contact_types.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 12 }}>
                  {primary.contact_types.map(t => typeMeta(t) && <span key={t} style={tagStyle(t)}>{typeMeta(t)!.label}</span>)}
                </div>
              )}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${c.line2}`, fontSize: 12, color: c.muted, lineHeight: 1.5 }}>
                Signs in to the vendor portal and is the default recipient for questionnaires and remediation requests.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 12 }}>
                {primary.email && <a href={`mailto:${primary.email}`} style={{ ...btn(), textDecoration: 'none' }}><Icon d={I.mail} size={13} />Email</a>}
                {canEdit && <button style={btn()} onClick={() => setEditing({ target: primary })}><Icon d={I.edit} size={13} />Edit</button>}
              </div>
            </>
          ) : (
            <div style={{ marginTop: 14, fontSize: 12.5, color: c.muted }}>
              No primary contact yet. {canEdit && <button style={linkBtn(isDark)} onClick={() => setEditing({ target: 'new' })}>Add one</button>}
            </div>
          )}
        </div>

        <div style={card(isDark)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink }}>Key roles</span>
            <span style={{ fontSize: 12, color: c.muted }}>Who to reach for what. Fill missing roles before you need them.</span>
            <span style={{ marginLeft: 'auto', fontWeight: 700, color: c.ink }}>{coveredCount} / {slots.length}</span>
          </div>
          <div style={{ height: 6, borderRadius: 99, background: c.line2, marginTop: 10, overflow: 'hidden' }}>
            <div style={{ width: `${(coveredCount / slots.length) * 100}%`, height: '100%', background: coveredCount === slots.length ? '#16A34A' : coveredCount >= slots.length / 2 ? '#16A34A' : '#D97706' }} />
          </div>
          <div className="vc-slots" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginTop: 14 }}>
            {slots.map(slot => {
              const person = slot.people[0];
              const missing = !person;
              const pill = slot.key === 'primary' ? <span style={tagPill(c.accentBg, c.accentInk)}>Primary</span> : <span style={tagStyle(slot.key)}>{slot.label}</span>;
              return (
                <button key={slot.key}
                  onClick={() => missing
                    ? (canEdit && setEditing({ target: 'new', preset: slot.key === 'primary' ? undefined : slot.key }))
                    : slot.people.length === 1 || slot.key === 'primary' ? setViewingId(person.id) : setFilter(slot.key as ContactType)}
                  title={missing ? (canEdit ? `Add ${/^[aeiou]/i.test(slot.label) ? 'an' : 'a'} ${slot.label.toLowerCase()} contact` : 'Not assigned') : undefined}
                  style={{ textAlign: 'left', border: `1px ${missing ? 'dashed' : 'solid'} ${missing ? (isDark ? '#5b4a1a' : '#FCD34D') : c.line}`, borderRadius: 10, padding: 12,
                    background: missing ? (isDark ? '#1a1406' : '#FFFCF5') : c.surface, cursor: missing && !canEdit ? 'default' : 'pointer', fontFamily: sans, minWidth: 0 }}>
                  {pill}
                  {missing ? (
                    <>
                      <span style={{ display: 'block', fontWeight: 600, marginTop: 8, color: isDark ? '#FBBF24' : '#B45309', fontSize: 12.5 }}>Not assigned</span>
                      {canEdit && <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: c.accent, marginTop: 2 }}>+ Add {slot.label.toLowerCase()} contact</span>}
                    </>
                  ) : (
                    <>
                      <span style={{ display: 'block', fontWeight: 600, marginTop: 8, color: c.ink, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person.name || person.email}</span>
                      <span style={{ display: 'block', fontSize: 12, color: c.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {slot.people.length > 1 ? `+${slot.people.length - 1} more` : person.role || ' '}
                      </span>
                    </>
                  )}
                </button>
              );
            })}
            <div style={{ border: `1px solid ${c.line}`, borderRadius: 10, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 12, color: c.muted }}>
              Technical and Executive contacts are optional
            </div>
          </div>
        </div>
      </div>

      {/* 4 Directory */}
      <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
        {contacts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', margin: '0 auto 10px', background: c.soft, color: c.faint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={I.users} size={20} /></div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.ink }}>Add the people you work with</div>
            <div style={{ fontSize: 12.5, color: c.muted, marginTop: 4 }}>Start with who to call about security issues and incidents at this vendor.</div>
            {canEdit && <button style={{ ...btn('primary'), marginTop: 14 }} onClick={() => setEditing({ target: 'new', preset: 'security' })}><Icon d={I.plus} size={13} />Add contact</button>}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: `1px solid ${c.line}`, flexWrap: 'wrap' }}>
              <div style={{ flex: '0 1 280px', position: 'relative', minWidth: 180 }}>
                <Icon d={I.search} size={14} style={{ position: 'absolute', left: 11, top: 10, color: c.faint }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email or job title" aria-label="Search contacts"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 32px', borderRadius: 8, border: `1px solid ${c.line}`, background: c.surface, color: c.ink, fontSize: 12.5, fontFamily: sans, outline: 'none' }} />
              </div>
              {[{ value: 'all' as const, label: 'All', count: contacts.length }, ...presentTypes.map(t => ({ value: t.value, label: t.label, count: contacts.filter(x => x.contact_types.includes(t.value)).length }))].map(f => {
                const on = filter === f.value;
                const base = f.value === 'all' ? null : tagStyle(f.value);
                return (
                  <button key={f.value} onClick={() => setFilter(f.value)} aria-pressed={on} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 99, fontSize: 12, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                    border: `1px solid ${on ? (base?.color as string || c.ink) : 'transparent'}`,
                    background: f.value === 'all' ? (on ? c.ink : c.soft) : base!.background, color: f.value === 'all' ? (on ? c.surface : c.ink) : base!.color,
                  }}>{f.label}<span style={{ opacity: 0.7 }}>{f.count}</span></button>
                );
              })}
              <span style={{ marginLeft: 'auto', display: 'inline-flex', border: `1px solid ${c.line}`, borderRadius: 8, overflow: 'hidden' }}>
                {(['table', 'cards'] as const).map(v => (
                  <button key={v} onClick={() => setView(v)} aria-pressed={view === v} style={{ padding: '6px 11px', fontSize: 12, fontWeight: 600, fontFamily: sans, cursor: 'pointer', border: 'none',
                    background: view === v ? c.ink : 'transparent', color: view === v ? c.surface : c.muted }}>{v === 'table' ? 'Table' : 'Cards'}</button>
                ))}
              </span>
            </div>

            {visible.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', fontSize: 12.5, color: c.muted }}>
                No contacts match. <button style={linkBtn(isDark)} onClick={() => { setSearch(''); setFilter('all'); }}>Clear filters</button>
              </div>
            ) : view === 'table' ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                  <thead><tr>
                    <th style={th}>Name</th><th style={th}>Responsibilities</th><th style={th}>Email</th><th style={th}>Phone</th><th style={th}>Involved in</th><th style={{ ...th, width: 40 }} aria-label="Actions" />
                  </tr></thead>
                  <tbody>
                    {visible.map(x => (
                      <tr key={x.id} className="vc-row" onClick={() => setViewingId(x.id)} style={{ cursor: 'pointer', background: viewingId === x.id ? c.sel : 'transparent' }}>
                        <td style={td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Avatar x={x} />
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: c.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
                                {x.name || x.email || 'Unnamed contact'}
                                {x.is_primary && <span style={{ ...tagPill(c.accentBg, c.accentInk), fontSize: 10, padding: '1px 6px' }}>★ Primary</span>}
                              </div>
                              <div style={{ fontSize: 12, color: x.role ? c.muted : c.faint }}>{x.role || 'No job title'}</div>
                            </div>
                          </div>
                        </td>
                        <td style={td}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {x.contact_types.length ? x.contact_types.map(t => typeMeta(t) && <span key={t} style={tagStyle(t)}>{typeMeta(t)!.label}</span>) : <span style={{ color: c.faint }}>—</span>}
                          </div>
                        </td>
                        <td style={td} onClick={e => e.stopPropagation()}>
                          {x.email ? <a href={`mailto:${x.email}`} style={{ color: c.accent, textDecoration: 'none' }}>{x.email}</a> : <span style={{ color: c.faint }}>No email</span>}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          {x.phone ? <a href={`tel:${x.phone.replace(/[^\d+]/g, '')}`} style={{ color: c.ink, textDecoration: 'none' }}>{x.phone}</a> : <span style={{ color: c.faint }}>—</span>}
                        </td>
                        <td style={{ ...td, color: c.muted }}>{involvementText(x)}</td>
                        <td style={td}><RowMenu contact={x} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, padding: 14 }}>
                {visible.map(x => (
                  <div key={x.id} className="vc-card" onClick={() => setViewingId(x.id)}
                    style={{ border: `1px solid ${c.line}`, borderRadius: 10, padding: '14px 16px', background: c.surface, cursor: 'pointer', display: 'flex', flexDirection: 'column', transition: 'border-color .12s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                      <Avatar x={x} size={38} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.name || x.email || 'Unnamed contact'}</span>
                          {x.is_primary && <span style={{ ...tagPill(c.accentBg, c.accentInk), fontSize: 10, padding: '1px 6px' }}>★ Primary</span>}
                        </div>
                        <div style={{ fontSize: 12, color: x.role ? c.muted : c.faint, marginTop: 1 }}>{x.role || 'No job title'}</div>
                      </div>
                      <RowMenu contact={x} />
                    </div>
                    {x.contact_types.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 11 }}>
                        {x.contact_types.map(t => typeMeta(t) && <span key={t} style={tagStyle(t)}>{typeMeta(t)!.label}</span>)}
                      </div>
                    )}
                    <div style={{ display: 'grid', gap: 4, marginTop: 11, fontSize: 12.5 }} onClick={e => e.stopPropagation()}>
                      {x.email
                        ? <a href={`mailto:${x.email}`} style={{ display: 'flex', alignItems: 'center', gap: 7, color: c.accent, textDecoration: 'none', overflow: 'hidden' }}><Icon d={I.mail} size={13} /><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{x.email}</span></a>
                        : <span style={{ display: 'flex', alignItems: 'center', gap: 7, color: c.faint }}><Icon d={I.mail} size={13} />No email</span>}
                      {x.phone && <a href={`tel:${x.phone.replace(/[^\d+]/g, '')}`} style={{ display: 'flex', alignItems: 'center', gap: 7, color: c.ink, textDecoration: 'none' }}><Icon d={I.phone} size={13} />{x.phone}</a>}
                    </div>
                    {x.notes && (
                      <div style={{ display: 'flex', gap: 7, marginTop: 9, fontSize: 12, color: c.muted, lineHeight: 1.45 }}>
                        <Icon d={I.note} size={13} style={{ marginTop: 2 }} />
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={x.notes}>{x.notes}</span>
                      </div>
                    )}
                    <div style={{ marginTop: 'auto', paddingTop: 11, fontSize: 11, color: c.faint }}>{involvementText(x) === '—' ? (x.is_primary ? 'Primary contact on the vendor record' : `Added ${relative(x.created_at)}`) : `Involved in ${involvementText(x)}`}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 5 Contact panel */}
      {viewing && !editing && (
        <ContactPanel
          contact={viewing}
          involvement={involvementOf(viewing)}
          isDark={isDark} c={c} tagStyle={tagStyle} btn={btn}
          avatar={<Avatar x={viewing} size={48} />}
          canEdit={canEdit} canDelete={canDelete}
          onClose={() => setViewingId(null)}
          onCopy={copy}
          onEdit={() => setEditing({ target: viewing })}
          onMakePrimary={() => makePrimary(viewing)}
          onDelete={() => { setDeleting(viewing); setDeleteError(''); }}
          onOpenItem={item => (item.kind === 'remediation' ? onOpenRemediation?.(item.id) : onOpenQuestionnaires?.())}
        />
      )}

      {editing && (
        <ContactDrawer
          target={editing.target}
          preset={editing.preset}
          vendorName={vendorName}
          isDark={isDark}
          c={c}
          tagStyle={tagStyle}
          onClose={() => setEditing(null)}
          onSave={async body => {
            const id = editing.target === 'new' ? null : editing.target.id;
            await saveMutation.mutateAsync({ id, body });
            setEditing(null);
            setToast(id ? 'Contact updated' : 'Contact added');
          }}
        />
      )}

      {deleting && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: 16 }}
          onMouseDown={e => { if (e.target === e.currentTarget && !deleteMutation.isPending) setDeleting(null); }}>
          <div role="dialog" aria-modal="true" aria-label="Delete contact" style={{ width: '100%', maxWidth: 420, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10, padding: 20, fontFamily: sans }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.ink, marginBottom: 6 }}>Delete contact</div>
            <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.5 }}>
              Remove <strong style={{ color: c.ink }}>{deleting.name || deleting.email}</strong> from this vendor's contacts? This can't be undone.
            </div>
            {deleteError && <div style={{ fontSize: 12, color: c.red, marginTop: 10 }} role="alert">{deleteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btn()} onClick={() => setDeleting(null)} disabled={deleteMutation.isPending}>Cancel</button>
              <button style={{ ...btn('primary'), background: T.red, borderColor: T.red, opacity: deleteMutation.isPending ? 0.7 : 1 }} onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? 'Deleting…' : 'Delete contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, padding: '9px 16px', borderRadius: 8,
          background: isDark ? '#1e293b' : T.ink, color: '#fff', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, animation: 'vcFade .2s ease-out', fontFamily: sans,
        }}><Icon d={I.check} size={13} />{toast}</div>
      )}
    </div>
  );
}

const tagPill = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: sans, color: fg, background: bg, whiteSpace: 'nowrap',
});

function InfoLine({ label, value, href, c, onCopy }: { label: string; value: string | null; href?: string; c: any; onCopy?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span style={{ width: 46, flexShrink: 0, fontSize: 12, color: c.muted }}>{label}</span>
      {value
        ? <a href={href} style={{ color: label === 'Email' ? c.accent : c.ink, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</a>
        : <span style={{ color: c.faint }}>Not recorded</span>}
      {value && onCopy && (
        <button onClick={onCopy} style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: sans, fontSize: 12, fontWeight: 600, color: c.accent }}>Copy</button>
      )}
    </div>
  );
}

/* ── Contact panel (read view; Edit opens the drawer below) ──────────── */
function ContactPanel({ contact, involvement, isDark, c, tagStyle, btn, avatar, canEdit, canDelete, onClose, onCopy, onEdit, onMakePrimary, onDelete, onOpenItem }: {
  contact: Contact; involvement: Involvement[]; isDark: boolean; c: any; tagStyle: (t: ContactType) => React.CSSProperties;
  btn: (kind?: 'default' | 'primary' | 'ghost' | 'danger') => React.CSSProperties; avatar: React.ReactNode;
  canEdit: boolean; canDelete: boolean; onClose: () => void; onCopy: (text: string, what: string) => void;
  onEdit: () => void; onMakePrimary: () => void; onDelete: () => void; onOpenItem: (item: Involvement) => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      // Clicking another row switches the panel (the row's own click handles it), so don't close on table clicks.
      if (panelRef.current && !panelRef.current.contains(t) && !t.closest('.vc-row, .vc-card, [role="dialog"]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    const timer = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); clearTimeout(timer); };
  }, [onClose]);
  const label: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: c.muted, margin: '18px 0 6px' };
  const kindPill = (k: Involvement['kind']) => k === 'remediation'
    ? { bg: isDark ? 'rgba(124,58,237,.18)' : '#F5F3FF', fg: isDark ? '#C4B5FD' : '#7C3AED', text: 'Remediation' }
    : { bg: c.accentBg, fg: c.accentInk, text: 'Questionnaire' };

  return (
    <div ref={panelRef} role="dialog" aria-modal="false" aria-label={contact.name || 'Contact'} style={{
      position: 'fixed', top: 64, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 1300, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans, animation: 'vcDrawerIn .2s ease-out',
    }}>
      <div style={{ padding: '18px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        {avatar}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16.5, fontWeight: 700, color: c.ink, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {contact.name || contact.email || 'Unnamed contact'}
            {contact.is_primary && <span style={tagPill(c.accentBg, c.accentInk)}>★ Primary</span>}
          </div>
          <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
            {contact.role || 'No job title'}{!contact.is_primary && contact.created_at ? ` · added ${relative(contact.created_at)}` : ''}
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', color: c.muted, cursor: 'pointer', padding: 4 }}><Icon d={I.close} size={16} /></button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 20px' }}>
        {contact.contact_types.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 16 }}>
            {contact.contact_types.map(t => typeMeta(t) && <span key={t} style={tagStyle(t)} title={typeMeta(t)!.hint}>{typeMeta(t)!.label}</span>)}
          </div>
        )}
        <div style={{ display: 'grid', gap: 9, marginTop: 16, fontSize: 12.5, border: `1px solid ${c.line}`, borderRadius: 10, padding: '12px 14px' }}>
          <InfoLine label="Email" value={contact.email} href={contact.email ? `mailto:${contact.email}` : undefined} c={c} onCopy={contact.email ? () => onCopy(contact.email!, 'Email') : undefined} />
          <InfoLine label="Phone" value={contact.phone} href={contact.phone ? `tel:${contact.phone.replace(/[^\d+]/g, '')}` : undefined} c={c} onCopy={contact.phone ? () => onCopy(contact.phone!, 'Phone') : undefined} />
        </div>
        {contact.is_primary && (
          <div style={{ marginTop: 10, fontSize: 12, color: c.muted, lineHeight: 1.5 }}>
            The primary contact signs in to the vendor portal and is the default recipient for questionnaires and remediation requests.
          </div>
        )}

        <div style={label}>Notes</div>
        <div style={{ fontSize: 12.5, color: contact.notes ? c.ink : c.faint, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{contact.notes || 'No notes.'}</div>

        <div style={label}>Involved in</div>
        {!contact.email ? (
          <div style={{ fontSize: 12.5, color: c.faint }}>Add an email to see the questionnaires and remediation requests this person received.</div>
        ) : involvement.length === 0 ? (
          <div style={{ fontSize: 12.5, color: c.faint }}>No questionnaires or remediation requests sent to this email yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {involvement.map(item => {
              const k = kindPill(item.kind);
              return (
                <button key={`${item.kind}-${item.id}`} onClick={() => onOpenItem(item)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, border: `1px solid ${c.line}`, borderRadius: 8, padding: '8px 10px', background: c.surface, cursor: 'pointer', fontFamily: sans, textAlign: 'left' }}>
                  <span style={tagPill(k.bg, k.fg)}>{k.text}</span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                  <span style={{ fontSize: 11.5, color: c.muted, whiteSpace: 'nowrap' }}>{statusLabel(item.status)}{item.at ? ` · ${relative(item.at)}` : ''}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {contact.email && <a href={`mailto:${contact.email}`} style={{ ...btn('primary'), textDecoration: 'none' }}><Icon d={I.mail} size={13} />Email</a>}
        {canEdit && <button style={btn()} onClick={onEdit}><Icon d={I.edit} size={13} />Edit</button>}
        {canEdit && !contact.is_primary && <button style={btn()} onClick={onMakePrimary}><Icon d={I.star} size={13} />Make primary</button>}
        {canDelete && !contact.is_primary && <button style={{ ...btn('danger'), marginLeft: 'auto' }} onClick={onDelete}><Icon d={I.trash} size={13} />Delete</button>}
      </div>
    </div>
  );
}

/* ── Add / edit drawer ─────────────────────────────────────────────── */
function ContactDrawer({ target, preset, vendorName, isDark, c, tagStyle, onClose, onSave }: {
  target: Contact | 'new'; preset?: ContactType; vendorName?: string; isDark: boolean; c: any;
  tagStyle: (t: ContactType) => React.CSSProperties;
  onClose: () => void; onSave: (body: Record<string, unknown>) => Promise<void>;
}) {
  const isNew = target === 'new';
  const [f, setF] = useState<Draft>(() => isNew
    ? { ...EMPTY, contact_types: preset ? [preset] : [] }
    : { name: target.name || '', role: target.role || '', email: target.email || '', phone: target.phone || '', contact_types: target.contact_types, notes: target.notes || '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    const onDown = (e: MouseEvent) => { if (panelRef.current && !panelRef.current.contains(e.target as Node) && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); clearTimeout(t); };
  }, [onClose, saving]);

  const set = (p: Partial<Draft>) => { setF(prev => ({ ...prev, ...p })); setError(''); };
  const toggleType = (t: ContactType) => set({ contact_types: f.contact_types.includes(t) ? f.contact_types.filter(x => x !== t) : [...f.contact_types, t] });

  const submit = async () => {
    if (!f.name.trim()) { setError('Enter a name.'); return; }
    if (f.email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) { setError('Enter a valid email address.'); return; }
    setSaving(true);
    try {
      await onSave({ name: f.name, role: f.role, email: f.email, phone: f.phone, contact_types: f.contact_types, notes: f.notes });
    } catch (e) {
      setError(apiError(e, 'Could not save the contact. Try again.'));
      setSaving(false);
    }
  };

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: sans, color: c.ink,
    background: c.surface, border: `1px solid ${c.line}`, borderRadius: 7, outline: 'none',
  };
  const label: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: c.muted, marginBottom: 5 };

  return (
    <div ref={panelRef} role="dialog" aria-modal="true" aria-label={isNew ? 'Add contact' : 'Edit contact'} style={{
      position: 'fixed', top: 64, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 1300, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans, animation: 'vcDrawerIn .2s ease-out',
    }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          {vendorName && <div style={{ fontSize: 11, color: c.faint, fontWeight: 600 }}>{vendorName}</div>}
          <div style={{ fontSize: 15.5, fontWeight: 700, color: c.ink, marginTop: 1 }}>{isNew ? 'Add contact' : 'Edit contact'}</div>
          {!isNew && target.is_primary && <div style={{ fontSize: 12, color: c.muted, marginTop: 3 }}>This is the primary contact. Changes update the vendor record too.</div>}
        </div>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', color: c.muted, cursor: 'pointer', padding: 4 }}><Icon d={I.close} size={16} /></button>
      </div>

      <form noValidate onSubmit={e => { e.preventDefault(); if (!saving) submit(); }} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'grid', gap: 16, alignContent: 'start' }}>
          <div>
            <label style={label} htmlFor="vc-name">Name</label>
            <input id="vc-name" style={input} value={f.name} autoFocus maxLength={200} placeholder="Jane Doe" onChange={e => set({ name: e.target.value })} />
          </div>
          <div>
            <label style={label} htmlFor="vc-role">Job title</label>
            <input id="vc-role" style={input} value={f.role} maxLength={120} placeholder="Head of security" onChange={e => set({ role: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label} htmlFor="vc-email">Email</label>
              <input id="vc-email" style={input} type="email" value={f.email} maxLength={254} placeholder="jane@vendor.com" onChange={e => set({ email: e.target.value })} />
            </div>
            <div>
              <label style={label} htmlFor="vc-phone">Phone</label>
              <PhoneNumberInput id="vc-phone" inputStyle={input} value={f.phone} placeholder="555 0100" onChange={v => set({ phone: v })} />
            </div>
          </div>
          <div>
            <div style={label}>Responsibilities</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {TYPES.map(t => {
                const on = f.contact_types.includes(t.value);
                return (
                  <button key={t.value} type="button" onClick={() => toggleType(t.value)} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 11px', borderRadius: 7, cursor: 'pointer', fontFamily: sans, textAlign: 'left',
                    border: `1px solid ${on ? c.accent : c.line}`, background: on ? c.accentBg : c.surface,
                  }}>
                    <span style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: `1.5px solid ${on ? c.accent : c.faint}`, background: on ? c.accent : 'transparent', color: '#fff',
                    }}>{on && <Icon d={I.check} size={11} />}</span>
                    <span style={{ flex: 1 }}>
                      <span style={tagStyle(t.value)}>{t.label}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: c.muted, marginTop: 3 }}>{t.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label style={label} htmlFor="vc-notes">Notes</label>
            <textarea id="vc-notes" rows={3} maxLength={2000} style={{ ...input, resize: 'vertical' }} value={f.notes}
              placeholder="Prefers email, available 9am to 6pm IST" onChange={e => set({ notes: e.target.value })} />
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, fontSize: 12, color: c.red }} role="alert">{error}</span>
          <button type="button" onClick={onClose} disabled={saving} style={{ padding: '7px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: `1px solid ${c.line}`, background: c.surface, color: c.ink, cursor: 'pointer', fontFamily: sans }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ padding: '7px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 'none', background: isDark ? '#3B82F6' : T.ink, color: '#fff', cursor: 'pointer', fontFamily: sans, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : isNew ? 'Add contact' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
